import {ERRORS} from '@github-ui/item-picker-base/constants/errors'
import {VALUES} from '@github-ui/item-picker-base/constants/values'
import {type ExtendedItemProps, ItemPicker} from '@github-ui/item-picker-base/ItemPicker'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useDebounce} from '@github-ui/use-debounce'
import {TriangleDownIcon} from '@primer/octicons-react'
import {Button, type SelectPanelProps, VisuallyHidden} from '@primer/react'
import type React from 'react'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import type {RepositoryPickerOwner} from '../utils/get-repository-search-query'
import {fetchSearchRepositories, fetchTopRepositories} from './fetch-org-repository-picker-items'
import {
  type OrgRepositoryPickerRepository as OrgRepositoryPickerRepositoryType,
  repositoryItemKey,
} from './org-repository-picker-types'
import {destinationAccessibleName, RepositoryDestinationVisual} from './org-repository-picker-visuals'
import styles from './OrgRepositoryPicker.module.css'

export type OrgRepositoryPickerRepository = OrgRepositoryPickerRepositoryType

const PLACEHOLDER = 'Select repository'
const SEARCH_PLACEHOLDER = 'Search'

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError'
}

export type OrgRepositoryPickerProps = {
  initialSelection?: OrgRepositoryPickerRepository
  onSelect: (selection: OrgRepositoryPickerRepository | undefined) => void
  anchorElement?: (props: React.HTMLAttributes<HTMLElement>) => React.ReactElement
  /** Restricts repositories to a single owner. Omit to fetch and search across owners. */
  scope?: RepositoryPickerOwner
}

// TODO: Rename to RepositorySwitcher now that it only offers repositories.
export function OrgRepositoryPicker({initialSelection, onSelect, anchorElement, scope}: OrgRepositoryPickerProps) {
  const {addToast} = useToastContext()

  const [selection, setSelection] = useState<OrgRepositoryPickerRepository | undefined>(initialSelection)
  const [topRepositories, setTopRepositories] = useState<OrgRepositoryPickerRepository[]>([])
  const [searchResults, setSearchResults] = useState<OrgRepositoryPickerRepository[] | undefined>(undefined)
  const [filter, setFilter] = useState('')
  const [isLoadingTopRepositories, setIsLoadingTopRepositories] = useState(true)
  const [isSearching, setIsSearching] = useState(false)
  const loading = isLoadingTopRepositories || isSearching

  const filterRef = useRef(filter)
  const searchAbortRef = useRef<AbortController | undefined>(undefined)
  const latestSearchRef = useRef(0)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const repos = await fetchTopRepositories(scope)
        if (cancelled) return
        setTopRepositories(repos)
      } catch {
        if (cancelled) return
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({type: 'error', message: ERRORS.couldNotSearchRepositories})
      } finally {
        if (!cancelled) setIsLoadingTopRepositories(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [addToast, scope])

  useEffect(() => {
    return () => {
      latestSearchRef.current += 1
      searchAbortRef.current?.abort()
    }
  }, [])

  const runSearch = useCallback(
    (searchQuery: string) => {
      searchAbortRef.current?.abort()
      const trimmed = searchQuery.trim()
      if (trimmed === '') {
        latestSearchRef.current += 1
        setSearchResults(undefined)
        setIsSearching(false)
        return
      }

      const requestId = latestSearchRef.current + 1
      latestSearchRef.current = requestId
      const controller = new AbortController()
      searchAbortRef.current = controller
      setIsSearching(true)

      void (async () => {
        try {
          const repos = await fetchSearchRepositories(trimmed, scope)
          if (requestId === latestSearchRef.current && trimmed === filterRef.current.trim()) {
            setSearchResults(repos)
            setIsSearching(false)
          }
        } catch (error) {
          if (requestId !== latestSearchRef.current) return
          setIsSearching(false)
          if (isAbortError(error)) return
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({type: 'error', message: ERRORS.couldNotSearchRepositories})
        } finally {
          if (searchAbortRef.current === controller) searchAbortRef.current = undefined
        }
      })()
    },
    [addToast, scope],
  )

  const debouncedSearch = useDebounce(runSearch, VALUES.pickerDebounceTime)

  const filterItems = useCallback(
    (value: string) => {
      const trimmed = value.trim()
      if (filter === trimmed) return
      setFilter(trimmed)
      filterRef.current = trimmed
      debouncedSearch(trimmed)
    },
    [filter, debouncedSearch],
  )

  // Repos come from the search endpoint, or top repos when not searching.
  const items = searchResults ?? topRepositories

  const convertToItemProps = useCallback(
    (repo: OrgRepositoryPickerRepository): ExtendedItemProps<OrgRepositoryPickerRepository> => ({
      id: repositoryItemKey(repo),
      text: repo.nameWithOwner,
      source: repo,
      leadingVisual: () => <RepositoryDestinationVisual repository={repo} />,
      // Type-qualifies the option name and announces visibility; the repo/lock icon is decorative.
      trailingVisual: <VisuallyHidden>{repo.isPrivate ? 'private repository' : 'repository'}</VisuallyHidden>,
      className: styles.Item,
    }),
    [],
  )

  const initialSelectedItems = useMemo(() => (initialSelection ? [initialSelection] : []), [initialSelection])

  const pickerRef = useRef<HTMLButtonElement>(null)

  const renderAnchor = useCallback(
    (anchorProps: React.HTMLAttributes<HTMLElement>) => {
      if (anchorElement) return anchorElement(anchorProps)

      return (
        <Button
          leadingVisual={selection ? () => <RepositoryDestinationVisual repository={selection} /> : undefined}
          trailingVisual={TriangleDownIcon}
          aria-label={selection ? destinationAccessibleName(selection) : PLACEHOLDER}
          {...anchorProps}
          ref={pickerRef}
        >
          <span>{selection?.nameWithOwner ?? PLACEHOLDER}</span>
        </Button>
      )
    },
    [anchorElement, selection],
  )

  const onSelectionChange = useCallback(
    ([firstItem]: OrgRepositoryPickerRepository[]) => {
      setSelection(firstItem)
      onSelect(firstItem)
    },
    [onSelect],
  )

  const showNoMatchMessage = !loading && items.length === 0
  const message = useMemo<SelectPanelProps['message'] | undefined>(
    () =>
      showNoMatchMessage
        ? {
            title: 'No repositories match',
            body: 'Try searching with a different query for results.',
            variant: 'empty',
          }
        : undefined,
    [showNoMatchMessage],
  )

  return (
    <ItemPicker
      items={items}
      initialSelectedItems={initialSelectedItems}
      filterItems={filterItems}
      getItemKey={repositoryItemKey}
      convertToItemProps={convertToItemProps}
      placeholderText={SEARCH_PLACEHOLDER}
      selectionVariant="single"
      onSelectionChange={onSelectionChange}
      loading={loading}
      renderAnchor={renderAnchor}
      selectPanelRef={pickerRef}
      resultListAriaLabel="Repository results"
      height="medium"
      width="medium"
      // Visually hide the SelectPanel's heading while keeping it as the dialog's accessible name.
      title={<VisuallyHidden>{PLACEHOLDER}</VisuallyHidden>}
      improvedNoMatchAccessibility={showNoMatchMessage}
      noMatchMessage={message}
    />
  )
}
